import React, { ReactNode, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';

import './FieldWrapper.styles.scss';

export type FieldWrapperProps = {
  className?: string;
  name?: string;
  id?: string;
  disabled?: boolean;
  value?: any;
  required?: boolean;
  label?: ReactNode;
  labelChildren?: ReactNode;
  maxLength?: number;
  description?: ReactNode;
  hasError?: boolean;
  error?: string;
  infoPopupContent?: ReactNode;
  infoPopupIcon?: ReactNode;
  onLabelClick?: () => void;
};

const FieldWrapper: React.FC<FieldWrapperProps> = (props) => {
  const {
    className,
    children,
    name,
    id,
    disabled,
    value,
    label,
    labelChildren,
    required,
    maxLength,
    description,
    hasError,
    error,
    infoPopupContent,
    infoPopupIcon = <InformationcircleIcon />,
    onLabelClick,
  } = props;

  const classes = classNames('field-wrapper', className);

  const { t } = useTranslation();

  const count = useMemo(
    () => maxLength - (String(value)?.length || 0),
    [maxLength, value],
  );

  return (
    <div className={classes}>
      {(label || maxLength) && (
        <div className="field-wrapper__label-container">
          <label
            className={classNames({ required })}
            htmlFor={id ?? name}
            {...(name && {
              'aria-label': name,
            })}
          >
            <p
              className={classNames({
                clickable: onLabelClick || (!!id && !disabled),
              })}
              onClick={onLabelClick}
            >
              {label}
            </p>

            {infoPopupContent && (
              <ResolvePopup
                onHover
                className="field-wrapper__info-icon"
                popupContent={infoPopupContent}
              >
                {infoPopupIcon}
              </ResolvePopup>
            )}
          </label>

          {labelChildren}

          {maxLength && (
            <small
              className={classNames('max-length-text', {
                error: !count,
              })}
            >
              {t('charactersLeft', {
                count,
              })}
            </small>
          )}
        </div>
      )}

      {children}

      {hasError ? (
        <small className="field-wrapper__error">{t(error)}</small>
      ) : (
        description && (
          <small className="field-wrapper__description">{description}</small>
        )
      )}
    </div>
  );
};

export default FieldWrapper;
